/**
 * errorMessage — extraction sûre d'un message d'erreur affichable.
 *
 * POURQUOI CE MODULE EXISTE
 * -------------------------
 * Le backend FastAPI renvoie `detail` sous trois formes différentes :
 *   1. une chaîne          → detail: "Token invalide"
 *   2. un objet structuré  → detail: { error: "password_too_short",
 *                                      message: "Le mot de passe doit …" }
 *   3. un tableau (422)    → detail: [{ loc: [...], msg: "field required" }]
 *
 * Placer directement `error.response.data.detail` dans un state React puis le
 * rendre (`{error}`, `<AlertDescription>{msg}</AlertDescription>`, ou la
 * `description` d'un toast) fait planter l'application avec
 * « Objects are not valid as a React child » — et comme il n'y a pas d'Error
 * Boundary, l'utilisateur obtient un écran blanc.
 *
 * RÈGLE : ne JAMAIS mettre un `detail` brut dans un state ou une prop rendue.
 * Toujours passer par `getApiErrorMessage(error)` (erreur axios) ou
 * `extractErrorMessage(detail)` (payload déjà déballé, ex. fetch + res.json()).
 */

export const DEFAULT_ERROR_MESSAGE =
  "Une erreur est survenue. Veuillez réessayer.";

/**
 * Normalise n'importe quelle valeur `detail` en chaîne affichable.
 *
 * @param {*} detail - valeur brute (string, objet, tableau, null…)
 * @param {string} [fallback] - message affiché si rien d'exploitable
 * @returns {string} une chaîne, toujours
 */
export function extractErrorMessage(detail, fallback = DEFAULT_ERROR_MESSAGE) {
  // 1. Rien d'exploitable
  if (detail === null || detail === undefined) return fallback;

  // 2. Chaîne directe
  if (typeof detail === "string") {
    const trimmed = detail.trim();
    return trimmed.length > 0 ? trimmed : fallback;
  }

  // 3. Nombre / booléen : convertible sans risque
  if (typeof detail === "number" || typeof detail === "boolean") {
    return String(detail);
  }

  // 4. Tableau (erreurs de validation Pydantic 422) → premier message
  if (Array.isArray(detail)) {
    for (const item of detail) {
      const message = extractErrorMessage(item, "");
      if (message) return message;
    }
    return fallback;
  }

  // 5. Objet structuré → on cherche une clé texte connue
  if (typeof detail === "object") {
    // `msg` = format Pydantic, `message`/`detail`/`error` = format maison
    const candidates = [detail.message, detail.msg, detail.detail, detail.error];
    for (const candidate of candidates) {
      if (typeof candidate === "string" && candidate.trim().length > 0) {
        return candidate.trim();
      }
    }
    return fallback;
  }

  return fallback;
}

/**
 * Extrait un message affichable depuis une erreur axios (ou fetch enveloppée).
 *
 * Ordre de résolution :
 *   response.data.detail → response.data.message → error.message → fallback
 *
 * @param {*} error - erreur axios / Error / valeur inconnue
 * @param {string} [fallback] - message affiché si rien d'exploitable
 * @returns {string} une chaîne, toujours
 */
export function getApiErrorMessage(error, fallback = DEFAULT_ERROR_MESSAGE) {
  const data = error?.response?.data;

  if (data !== null && data !== undefined) {
    const fromDetail = extractErrorMessage(data.detail, "");
    if (fromDetail) return fromDetail;

    if (typeof data.message === "string" && data.message.trim().length > 0) {
      return data.message.trim();
    }

    // Certains backends renvoient directement une chaîne comme body
    if (typeof data === "string" && data.trim().length > 0) {
      return data.trim();
    }
  }

  if (typeof error?.message === "string" && error.message.trim().length > 0) {
    return error.message.trim();
  }

  return fallback;
}

export default getApiErrorMessage;
